import React, { useState, useMemo } from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import { PermitLetter, LetterGeneration, Employee, Branch } from '../../types';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { usePermitLetters } from '../../hooks/usePermitLetters';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import { RaaftFormState, buildLetterHtml, buildTemplatePrintHtml } from './templates';
import RaaftGeneratorForm from './GeneratorForm';

const INITIAL_FORM: RaaftFormState = {
  passportOffice: '', vesselId: '', flag: 'جزر القمر',
  ownerNationality: 'مصري', fromPort: '', currentLocation: '', selectedEmployeeIds: [],
};

const RaaftLetters: React.FC = () => {
  const { data: apiEmployees = [] } = useEmployees({ populate: '*' });
  const employees = apiEmployees as Employee[];
  const { data: apiBranches = [] } = useBranches({ populate: '*' });
  const branches = apiBranches as Branch[];
  const vessels = useMemo(() => branches.filter(b => b.type === 'vessel'), [branches]);

  const queryOptions = useMemo(() => ({ populate: '*' }), []);
  const { data: allPermitLetters = [], isLoading, error, refetch } = usePermitLetters(queryOptions);
  const letters = useMemo(() => allPermitLetters.filter(l => l.type === 'personnel'), [allPermitLetters]);

  const [generations] = useState<LetterGeneration[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createdByFilter, setCreatedByFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [activeTab, setActiveTab] = useState<'templates' | 'generations'>('templates');
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [generatedPreview, setGeneratedPreview] = useState('');
  const [form, setForm] = useState<RaaftFormState>(INITIAL_FORM);

  const uniqueCreatedBy = Array.from(new Set(letters.map(l => l.createdBy).filter(Boolean)));
  const uniqueGeneratedBy = Array.from(new Set(generations.map(g => g.generatedBy).filter(Boolean)));

  const filteredLetters = useMemo(() => letters.filter(letter => {
    const s = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || letter.templateName.toLowerCase().includes(s) || letter.subject.toLowerCase().includes(s) || letter.content.toLowerCase().includes(s) || letter.createdBy.toLowerCase().includes(s);
    const matchesType = typeFilter === 'all' || (typeFilter === 'default' && letter.isDefault) || (typeFilter === 'custom' && !letter.isDefault);
    const matchesCreatedBy = createdByFilter === 'all' || letter.createdBy === createdByFilter;
    const d = new Date(letter.createdAt);
    return matchesSearch && matchesType && matchesCreatedBy && (!dateFromFilter || d >= new Date(dateFromFilter)) && (!dateToFilter || d <= new Date(dateToFilter));
  }), [letters, searchTerm, typeFilter, createdByFilter, dateFromFilter, dateToFilter]);

  const filteredGenerations = useMemo(() => generations.filter(gen => {
    const letter = letters.find(l => l.id === gen.letterId);
    const s = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || (letter?.templateName?.toLowerCase().includes(s)) || (gen.notes?.toLowerCase().includes(s)) || gen.status.toLowerCase().includes(s) || gen.generatedBy.toLowerCase().includes(s);
    const d = new Date(gen.generatedDate);
    return matchesSearch && (statusFilter === 'all' || gen.status === statusFilter) && (createdByFilter === 'all' || gen.generatedBy === createdByFilter) && (!dateFromFilter || d >= new Date(dateFromFilter)) && (!dateToFilter || d <= new Date(dateToFilter));
  }), [generations, letters, searchTerm, statusFilter, createdByFilter, dateFromFilter, dateToFilter]);

  const makeTemplateExportData = () => filteredLetters.map(l => ({
    'Template Name': l.templateName, 'Subject': l.subject, 'Type': l.isDefault ? 'Default' : 'Custom',
    'Created By': l.createdBy, 'Created Date': new Date(l.createdAt).toLocaleDateString(),
    'Updated Date': new Date(l.updatedAt).toLocaleDateString(),
    'Content Preview': l.content.substring(0, 100) + (l.content.length > 100 ? '...' : '')
  }));

  const makeGenerationExportData = () => filteredGenerations.map(gen => {
    const letter = letters.find(l => l.id === gen.letterId);
    const names = gen.selectedItems.map(id => employees.find(e => e.id === id)?.name || 'Unknown');
    return { 'Template Name': letter?.templateName || 'Unknown', 'Selected Employees': names.join(', '),
      'Generated Date': new Date(gen.generatedDate).toLocaleDateString(), 'Generated By': gen.generatedBy,
      'Status': gen.status.charAt(0).toUpperCase() + gen.status.slice(1), 'Recipients': gen.recipients?.join(', ') || '', 'Notes': gen.notes || '' };
  });

  const handleExportTemplates = () => exportToExcel(makeTemplateExportData(), `immigration_signoff_templates_${new Date().toISOString().split('T')[0]}`);
  const handlePrintTemplates = () => printAsExcel(makeTemplateExportData(), 'Immigration Sign Off Templates');
  const handleExportGenerations = () => exportToExcel(makeGenerationExportData(), `immigration_signoff_generations_${new Date().toISOString().split('T')[0]}`);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePrintGenerations = () => printAsExcel(makeGenerationExportData(), 'Immigration Sign Off Generations');

  const handleGenerate = (_letter: PermitLetter) => { setIsGeneratorOpen(true); setGeneratedPreview(''); setForm(INITIAL_FORM); };

  const handleGeneratePreview = () => {
    const vessel = vessels.find(v => v.id === form.vesselId);
    const selectedEmps = employees.filter(e => form.selectedEmployeeIds.includes(e.id));
    setGeneratedPreview(buildLetterHtml(form, vessel?.name || '___________', selectedEmps));
  };

  const handlePrintLetter = () => {
    if (!generatedPreview) return;
    const w = window.open('', '_blank');
    if (w) { w.document.write(generatedPreview); w.document.close(); w.print(); }
  };

  const handleEdit = (letter: PermitLetter) => { alert(`Edit letter: ${letter.templateName}\n\nThis will open the letter editor where you can modify the template content, subject, and formatting.`); };

  const handlePrintTemplate = (letter: PermitLetter) => {
    const w = window.open('', '_blank');
    if (w) { w.document.write(buildTemplatePrintHtml(letter.subject, letter.content)); w.document.close(); w.print(); }
  };

  const handleEmail = (letter: PermitLetter) => {
    window.location.href = `mailto:?subject=${encodeURIComponent(letter.subject)}&body=${encodeURIComponent(`${letter.content}\n\nتم إرسال هذه الرسالة من نظام Petromarine ERP`)}`;
  };

  const clearFilters = () => { setSearchTerm(''); setTypeFilter('all'); setStatusFilter('all'); setCreatedByFilter('all'); setDateFromFilter(''); setDateToFilter(''); };

  const columns: TableColumn[] = [
    { key: 'templateName', label: 'Template Name', sortable: true },
    { key: 'subject', label: 'Subject', sortable: true },
    { key: 'isDefault', label: 'Default', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{value ? 'Yes' : 'No'}</span>
    )},
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'actions', label: 'Actions', sortable: false, render: (_: unknown, item: PermitLetter) => (
      <div className="flex items-center space-x-2">
        <button onClick={() => handleGenerate(item)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200">Generate</button>
        <button onClick={() => handleEdit(item)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200">Edit</button>
        <button onClick={() => handlePrintTemplate(item)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200">Print</button>
        <button onClick={() => handleEmail(item)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-purple-700 bg-purple-100 hover:bg-purple-200">Email</button>
      </div>
    )}
  ];

  const generationColumns: TableColumn[] = [
    { key: 'letterId', label: 'Template', sortable: true, render: (value: string) => letters.find(l => l.id === value)?.templateName || 'Unknown' },
    { key: 'selectedItems', label: 'Employees', sortable: false, render: (value: string[]) => {
      const n = value.map(id => employees.find(e => e.id === id)?.name || 'Unknown').join(', ');
      return n.length > 50 ? `${n.substring(0, 50)}...` : n;
    }},
    { key: 'generatedDate', label: 'Generated', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'generatedBy', label: 'Generated By', sortable: true },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value === 'sent' ? 'bg-green-100 text-green-800' : value === 'draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>{value.charAt(0).toUpperCase() + value.slice(1)}</span>
    )}
  ];

  if (isLoading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div></div>;
  if (error) return <ErrorState message={error.message} onRetry={refetch} entityName="raaft letters" />;

  if (isGeneratorOpen) {
    return <RaaftGeneratorForm form={form} setForm={setForm} vessels={vessels} employees={employees}
      generatedPreview={generatedPreview} onGeneratePreview={handleGeneratePreview} onPrintLetter={handlePrintLetter}
      onClose={() => { setIsGeneratorOpen(false); setGeneratedPreview(''); }} />;
  }

  const templateFilters = [
    { key: 'type', label: 'Type', type: 'select' as const, value: typeFilter, onChange: setTypeFilter, options: [{ value: 'all', label: 'All Types' }, { value: 'default', label: 'Default' }, { value: 'custom', label: 'Custom' }] },
    { key: 'createdBy', label: 'Created By', type: 'select' as const, value: createdByFilter, onChange: setCreatedByFilter, options: [{ value: 'all', label: 'All Users' }, ...uniqueCreatedBy.map(u => ({ value: u, label: u }))] },
    { key: 'dateFrom', label: 'Created From', type: 'date' as const, value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Created To', type: 'date' as const, value: dateToFilter, onChange: setDateToFilter },
  ];
  const generationFilters = [
    { key: 'status', label: 'Status', type: 'select' as const, value: statusFilter, onChange: setStatusFilter, options: [{ value: 'all', label: 'All Statuses' }, { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }, { value: 'pending', label: 'Pending' }] },
    { key: 'generatedBy', label: 'Generated By', type: 'select' as const, value: createdByFilter, onChange: setCreatedByFilter, options: [{ value: 'all', label: 'All Users' }, ...uniqueGeneratedBy.map(u => ({ value: u, label: u }))] },
    { key: 'dateFrom', label: 'Generated From', type: 'date' as const, value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Generated To', type: 'date' as const, value: dateToFilter, onChange: setDateToFilter },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Immigration Sign Off" description="Arabic immigration sign-off letters for crew departure (رفت)" />
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Automatic Letter Generation</h3>
            <p className="mt-2 text-sm text-blue-700">Raaft (رفت) letters are generated for crew departure from vessels. These letters are sent to the Passport Office to request immigration sign-off for departing crew members.</p>
          </div>
        </div>
      </div>
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button onClick={() => setActiveTab('templates')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'templates' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Letter Templates</button>
          <button onClick={() => setActiveTab('generations')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'generations' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Generated Letters</button>
        </nav>
      </div>
      {activeTab === 'templates' ? (
        <>
          <FilterBar searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search templates..." filters={templateFilters} onClearFilters={clearFilters} totalCount={letters.length} filteredCount={filteredLetters.length} entityName="Immigration Sign Off Templates" onExport={handleExportTemplates} onPrint={handlePrintTemplates} />
          <Table columns={columns} data={filteredLetters} onEdit={(letter) => handleGenerate(letter as PermitLetter)} showAddButton={false} searchable={false} />
        </>
      ) : (
        <>
          <FilterBar searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search generated letters..." filters={generationFilters} onClearFilters={clearFilters} totalCount={generations.length} filteredCount={filteredGenerations.length} entityName="Generated Immigration Sign Off Letters" onExport={handleExportGenerations} />
          <Table columns={generationColumns} data={filteredGenerations} showAddButton={false} showEditButton={false} showDeleteButton={false} searchable={false} />
        </>
      )}
    </div>
  );
};

export default RaaftLetters;
